import React, { forwardRef } from 'react';

interface InputFieldProps {
  label?: string;
  required?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  textarea?: boolean;
  rows?: number;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCharCount?: boolean;
  className?: string;
  inputClassName?: string;
  icon?: React.ReactNode;
  onIconClick?: () => void;
}

export const InputField = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputFieldProps>(
  (
    {
      label,
      required = false,
      value,
      onChange,
      placeholder,
      type = 'text',
      textarea = false,
      rows = 4,
      disabled = false,
      error,
      helperText,
      maxLength,
      showCharCount = false,
      className = '',
      inputClassName = '',
      icon,
      onIconClick,
    },
    ref,
  ) => {
    const baseInputClasses = `
      w-full p-2 px-3 
      border-[#4A082D] 
      font-normal italic 
      rounded-lg 
      bg-[#4A082D33] 
      placeholder-[#621C43] 
      text-[#4A082D]
      focus:outline-none 
      focus:ring-2 
      focus:ring-[#621C43] 
      focus:ring-opacity-50
      disabled:opacity-50 
      disabled:cursor-not-allowed
      ${error ? 'border-red-500' : ''}
      ${inputClassName}
    `
      .trim()
      .replace(/\s+/g, ' ');

    const containerClasses = `${className}`;

    return (
      <div className={containerClasses}>
        {/* Label */}
        {label && (
          <label className="block text-sm font-medium mb-1 text-[#621C43]">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input Container with Icon */}
        <div className="relative">
          {textarea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              rows={rows}
              disabled={disabled}
              maxLength={maxLength}
              className={baseInputClasses}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              type={type}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              className={baseInputClasses}
            />
          )}

          {/* Icon Button */}
          {icon && (
            <button
              type="button"
              onClick={onIconClick}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#621C43] hover:opacity-70"
            >
              {icon}
            </button>
          )}
        </div>

        {/* Character Count */}
        {/* {showCharCount && maxLength && (
          <p className="text-right text-sm text-[#621C43] mt-1">
            {value.length}/{maxLength} characters
          </p>
        )} */}

        {/* Helper Text */}
        {helperText && !error && <p className="text-sm text-[#621C43] mt-1">{helperText}</p>}

        {/* Error Message */}
        {error && <p className="text-sm text-red-500 font-medium">{error}</p>}
      </div>
    );
  },
);

InputField.displayName = 'InputField';
